import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';

const HeroSection = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
    }> = [];

    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
      });
    }

    const animate = () => {
      ctx.fillStyle = 'rgba(10, 14, 39, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 217, 255, 0.6)';
        ctx.fill();

        particles.forEach((particle2, j) => {
          if (i === j) return;
          const dx = particle.x - particle2.x;
          const dy = particle.y - particle2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(particle2.x, particle2.y);
            ctx.strokeStyle = `rgba(0, 217, 255, ${0.2 * (1 - distance / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-dark via-primary to-[#4A2C7F]"></div>
      
      {/* Particle Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 z-0"></canvas>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30 z-10"></div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-7xl font-black text-white leading-tight" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                ELEVATE YOUR
              </h1>
              <h2 className="text-6xl font-serif italic bg-gradient-to-r from-secondary to-[#FF00FF] bg-clip-text text-transparent">
                Digital Presence
              </h2>
            </div>

            <p className="text-xl text-white/80 max-w-xl leading-relaxed">
              Transform your business with data-driven digital marketing strategies that deliver measurable results and sustainable growth in today's competitive landscape.
            </p>

            <div className="flex flex-wrap gap-5">
              <Link
                to="/enquiry"
                className="px-8 py-4 bg-white text-dark font-semibold rounded-full hover:bg-secondary hover:text-white transition-all duration-300 shadow-2xl hover:shadow-secondary/50 hover:scale-105 whitespace-nowrap"
              >
                Start Growing
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 bg-white/10 backdrop-blur-md text-white font-semibold rounded-full border-2 border-white/30 hover:bg-white hover:text-dark transition-all duration-300 whitespace-nowrap"
              >
                View Our Work
              </Link>
            </div>

            {/* Floating Stats */}
            <div className="flex flex-wrap gap-6 pt-8">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/20">
                <div className="text-3xl font-bold text-white">200+</div>
                <div className="text-sm text-white/70">Clients</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/20">
                <div className="text-3xl font-bold text-white">98%</div>
                <div className="text-sm text-white/70">Success Rate</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/20">
                <div className="text-3xl font-bold text-white">5+</div>
                <div className="text-sm text-white/70">Years</div>
              </div>
            </div>
          </div>

          {/* Right 3D Element */}
          <div className="relative h-[600px] flex items-center justify-center">
            <div className="relative w-full h-full">
              {/* Holographic Sphere */}
              <div className="absolute inset-0 flex items-center justify-center animate-pulse">
                <div className="w-96 h-96 rounded-full bg-gradient-to-br from-secondary/30 to-primary/30 blur-3xl"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-80 h-80 rounded-full border-4 border-secondary/40 animate-spin" style={{ animationDuration: '20s' }}></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-64 h-64 rounded-full border-4 border-primary/40 animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 rounded-full bg-gradient-to-br from-secondary to-primary opacity-60 blur-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
